/**
 * Scrollable calendar picker — README §7. Shared by connect step 2 and the
 * Settings dialog's Calendars tab. An account can carry dozens of
 * calendars, so the list scrolls at a fixed height rather than growing the
 * page.
 */
import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import Input from "../../theme/untitled/Input/Input";
import Icon from "../../theme/untitled/icons/Icon";
import CalendarPickerRow from "./CalendarPickerRow";
import styles from "./CalendarPickerList.module.scss";

/**
 * @param {boolean=false} fixedHeight - the Settings dialog's Calendars tab
 *   caps the list at a fixed 300px ("the list scrolls, the dialog does not
 *   grow" — frame 20) instead of the standalone connect page's viewport-
 *   filling behavior.
 */
const CalendarPickerList = ({
  calendars,
  selected,
  onChange,
  onSelectAll,
  primaryCalendarId,
  fixedHeight = false,
}) => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const sorted = [...calendars].sort((a, b) => a.label.localeCompare(b.label));
    return term ? sorted.filter((c) => c.label.toLowerCase().includes(term)) : sorted;
  }, [calendars, search]);

  const allSelected = calendars.length > 0 && calendars.every((c) => selected[c.id]);

  const onSearchChange = useCallback((e) => setSearch(e.target.value), []);
  const onToggleSelectAll = useCallback(
    () => onSelectAll(!allSelected),
    [onSelectAll, allSelected]
  );

  const rows = filtered.map((calendar) => (
    <CalendarPickerRow
      key={calendar.id}
      calendar={calendar}
      isPrimary={Boolean(primaryCalendarId) && calendar.id === primaryCalendarId}
      checked={Boolean(selected[calendar.id])}
      onChange={onChange}
    />
  ));

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <Input
          size="sm"
          className={styles.search}
          iconLeading={<Icon name="SearchLg" size={16} />}
          placeholder="Search calendars"
          value={search}
          onChange={onSearchChange}
        />
        <button type="button" className={styles.selectAll} onClick={onToggleSelectAll}>
          {allSelected ? "Deselect all" : "Select all"}
        </button>
      </div>
      <ul className={classNames(styles.list, { [styles["list--fixed"]]: fixedHeight })}>
        {rows}
      </ul>
    </div>
  );
};

export default CalendarPickerList;
